import React, { useEffect, useState } from 'react';
import {
  Button,
  Form,
  Header,
  Label,
  Pagination,
  Segment,
  Select,
  Table,
  Popup,
  Checkbox,
} from 'semantic-ui-react';
import {
  API,
  copy,
  isAdmin,
  showError,
  showSuccess,
  showWarning,
  timestamp2string,
} from '../helpers';
import { useTranslation } from 'react-i18next';

import { ITEMS_PER_PAGE } from '../constants';
import { renderColorLabel, renderQuota } from '../helpers/render';
import { Link } from 'react-router-dom';

function renderTimestamp(timestamp, request_id) {
  return (
    <code
      onClick={async () => {
        if (await copy(request_id)) {
          showSuccess(`已复制请求 ID：${request_id}`);
        } else {
          showWarning(`请求 ID 复制失败：${request_id}`);
        }
      }}
      style={{ cursor: 'pointer' }}
    >
      {timestamp2string(timestamp)}
    </code>
  );
}

const MODE_OPTIONS = [
  { key: 'all', text: '全部用户', value: 'all' },
  { key: 'self', text: '当前用户', value: 'self' },
];

function renderType(type, t) {
  switch (type) {
    case 1:
      return (
        <Label basic color='green'>
          {t('log.type.topup')}
        </Label>
      );
    case 2:
      return (
        <Label basic color='olive'>
          {t('log.type.usage')}
        </Label>
      );
    case 3:
      return (
        <Label basic color='orange'>
          {t('log.type.admin')}
        </Label>
      );
    case 4:
      return (
        <Label basic color='purple'>
          {t('log.type.system')}
        </Label>
      );
    case 5:
      return (
        <Label basic color='violet'>
          {t('log.type.test')}
        </Label>
      );
    default:
      return (
        <Label basic color='black'>
          {t('log.type.unknown')}
        </Label>
      );
  }
}

function getColorByElapsedTime(elapsedTime) {
  if (elapsedTime === undefined || 0) return 'black';
  if (elapsedTime < 1000) return 'green';
  if (elapsedTime < 3000) return 'olive';
  if (elapsedTime < 5000) return 'yellow';
  if (elapsedTime < 10000) return 'orange';
  return 'red';
}

function renderDetail(log) {
  return (
    <>
      {/*{log.content}*/}
      {/*<br />*/}
      {log.elapsed_time && (
        <Label
          basic
          size={'mini'}
          color={getColorByElapsedTime(log.elapsed_time)}
        >
          {log.elapsed_time} ms
        </Label>
      )}
      {log.is_stream && (
        <>
          <Label size={'mini'} color='pink'>
            Stream
          </Label>
        </>
      )}
      {log.system_prompt_reset && (
        <>
          <Label basic size={'mini'} color='red'>
            System Prompt Reset
          </Label>
        </>
      )}
    </>
  );
}

const LogsTable = ({ trigger }) => {
  const { t } = useTranslation();
  const [logs, setLogs] = useState([]);
  const [showStat, setShowStat] = useState(false);
  const [loading, setLoading] = useState(true);
  const [activePage, setActivePage] = useState(1);
  const [searchKeyword, setSearchKeyword] = useState('');
  const [searching, setSearching] = useState(false);
  const [logType, setLogType] = useState(0);
  const isAdminUser = isAdmin();
  let now = new Date();
  const [inputs, setInputs] = useState({
    username: '',
    token_name: '',
    model_name: '',
    start_timestamp: timestamp2string(0),
    end_timestamp: timestamp2string(now.getTime() / 1000 + 3600),
    channel: '',
  });
  const {
    username,
    token_name,
    model_name,
    start_timestamp,
    end_timestamp,
    channel,
  } = inputs;

  const [stat, setStat] = useState({
    quota: 0,
    token: 0,
  });

  // 选择、分页显示数量与计算框状态
  const [itemsPerPage, setItemsPerPage] = useState(ITEMS_PER_PAGE);
  const [selectedKeys, setSelectedKeys] = useState(new Set());
  const [originalTotalQuota, setOriginalTotalQuota] = useState(null);
  const [routeTotalQuota, setRouteTotalQuota] = useState(null);
  const [decreasePercent, setDecreasePercent] = useState('');
  const [flashOriginal, setFlashOriginal] = useState(false);
  const [flashRoute, setFlashRoute] = useState(false);
  const [flashPercent, setFlashPercent] = useState(false);
  const [chooseTargetOpen, setChooseTargetOpen] = useState(false);
  const [nextFillTarget, setNextFillTarget] = useState('original');

  const getLogKey = (log) => {
    if (log.request_id) return log.request_id;
    return `${log.created_at}_${log.model_name || ''}_${log.token_name || ''}_${log.quota || 0}_${log.prompt_tokens || 0}_${log.completion_tokens || 0}`;
  };

  const LOG_OPTIONS = [
    { key: '0', text: t('log.type.all'), value: 0 },
    { key: '1', text: t('log.type.topup'), value: 1 },
    { key: '2', text: t('log.type.usage'), value: 2 },
    { key: '3', text: t('log.type.admin'), value: 3 },
    { key: '4', text: t('log.type.system'), value: 4 },
    { key: '5', text: t('log.type.test'), value: 5 },
  ];

  const handleInputChange = (e, { name, value }) => {
    setInputs((inputs) => ({ ...inputs, [name]: value }));
  };

  const getLogSelfStat = async () => {
    let localStartTimestamp = Date.parse(start_timestamp) / 1000;
    let localEndTimestamp = Date.parse(end_timestamp) / 1000;
    let res = await API.get(
      `/api/log/self/stat?type=${logType}&token_name=${token_name}&model_name=${model_name}&start_timestamp=${localStartTimestamp}&end_timestamp=${localEndTimestamp}`
    );
    const { success, message, data } = res.data;
    if (success) {
      setStat(data);
    } else {
      showError(message);
    }
  };

  const getLogStat = async () => {
    let localStartTimestamp = Date.parse(start_timestamp) / 1000;
    let localEndTimestamp = Date.parse(end_timestamp) / 1000;
    let res = await API.get(
      `/api/log/stat?type=${logType}&username=${username}&token_name=${token_name}&model_name=${model_name}&start_timestamp=${localStartTimestamp}&end_timestamp=${localEndTimestamp}&channel=${channel}`
    );
    const { success, message, data } = res.data;
    if (success) {
      setStat(data);
    } else {
      showError(message);
    }
  };

  const handleEyeClick = async () => {
    if (!showStat) {
      if (isAdminUser) {
        await getLogStat();
      } else {
        await getLogSelfStat();
      }
    }
    setShowStat(!showStat);
  };

  const showUserTokenQuota = () => {
    return logType !== 5;
  };

  const loadLogs = async (startIdx) => {
    let url = '';
    let localStartTimestamp = Date.parse(start_timestamp) / 1000;
    let localEndTimestamp = Date.parse(end_timestamp) / 1000;
    if (isAdminUser) {
      url = `/api/log/?p=${startIdx}&type=${logType}&username=${username}&token_name=${token_name}&model_name=${model_name}&start_timestamp=${localStartTimestamp}&end_timestamp=${localEndTimestamp}&channel=${channel}&page_size=${itemsPerPage}`;
    } else {
      url = `/api/log/self/?p=${startIdx}&type=${logType}&token_name=${token_name}&model_name=${model_name}&start_timestamp=${localStartTimestamp}&end_timestamp=${localEndTimestamp}&page_size=${itemsPerPage}`;
    }
    const res = await API.get(url);
    const { success, message, data } = res.data;
    if (success) {
      if (startIdx === 0) {
        setLogs(data);
      } else {
        let newLogs = [...logs];
        newLogs.splice(startIdx * itemsPerPage, data.length, ...data);
        setLogs(newLogs);
      }
    } else {
      showError(message);
    }
    setLoading(false);
  };

  const onPaginationChange = (e, { activePage }) => {
    (async () => {
      // 依据当前 UI 的 totalPages 计算哨兵页，保持与 Pagination 一致
      const sentinelPage =
        Math.ceil(logs.length / itemsPerPage) +
        (logs.length % itemsPerPage === 0 ? 1 : 0);

      if (activePage === sentinelPage && logs.length % itemsPerPage === 0) {
        // 需要按后端页大小加载更多数据
        const nextServerPageStartIdx = Math.floor(logs.length / itemsPerPage);
        await loadLogs(nextServerPageStartIdx);
      }
      setActivePage(activePage);
    })();
  };

  const refresh = async () => {
    setLoading(true);
    setActivePage(1);
    await loadLogs(0);
  };

  useEffect(() => {
    refresh().then();
  }, [logType, trigger, itemsPerPage]);

  const searchLogs = async () => {
    if (searchKeyword === '') {
      // if keyword is blank, load files instead.
      await loadLogs(0);
      setActivePage(1);
      return;
    }
    setSearching(true);
    const res = await API.get(`/api/log/self/search?keyword=${searchKeyword}`);
    const { success, message, data } = res.data;
    if (success) {
      setLogs(data);
      setActivePage(1);
    } else {
      showError(message);
    }
    setSearching(false);
  };

  const handleKeywordChange = async (e, { value }) => {
    setSearchKeyword(value.trim());
  };

  const sortLog = (key) => {
    if (logs.length === 0) return;
    setLoading(true);
    let sortedLogs = [...logs];
    if (typeof sortedLogs[0][key] === 'string') {
      sortedLogs.sort((a, b) => {
        return ('' + a[key]).localeCompare(b[key]);
      });
    } else {
      sortedLogs.sort((a, b) => {
        if (a[key] === b[key]) return 0;
        if (a[key] > b[key]) return -1;
        if (a[key] < b[key]) return 1;
      });
    }
    if (sortedLogs[0].id === logs[0].id) {
      sortedLogs.reverse();
    }
    setLogs(sortedLogs);
    setLoading(false);
  };

  // 当前页切片与全选状态
  const currentPageLogs = logs.slice(
    (activePage - 1) * itemsPerPage,
    activePage * itemsPerPage
  );
  const currentPageAllSelected =
    currentPageLogs.length > 0 &&
    currentPageLogs.every((log) => selectedKeys.has(getLogKey(log)));

  const toggleSelectAllCurrentPage = (e, { checked }) => {
    const newSet = new Set(selectedKeys);
    if (checked) {
      currentPageLogs.forEach((log) => {
        newSet.add(getLogKey(log));
      });
    } else {
      currentPageLogs.forEach((log) => {
        newSet.delete(getLogKey(log));
      });
    }
    setSelectedKeys(newSet);
  };

  const toggleSelectOne = (key, checked) => {
    const newSet = new Set(selectedKeys);
    if (checked) {
      newSet.add(key);
    } else {
      newSet.delete(key);
    }
    setSelectedKeys(newSet);
  };

  const computeSelectedQuota = () => {
    if (selectedKeys.size === 0) {
      showWarning(t('log.messages.need_selection'));
      return null;
    }
    let sum = 0;
    logs.forEach((log) => {
      const key = getLogKey(log);
      if (selectedKeys.has(key)) {
        if (log.quota !== null && log.quota !== undefined) {
          sum += log.quota;
        }
      }
    });
    return sum;
  };

  const handleCalcCost = () => {
    const sum = computeSelectedQuota();
    if (sum === null) return;
    if (originalTotalQuota === null || originalTotalQuota === 0) {
      setOriginalTotalQuota(sum);
      setNextFillTarget('route');
      setFlashOriginal(true);
      setTimeout(() => setFlashOriginal(false), 800);
      return;
    }
    if (routeTotalQuota === null) {
      setRouteTotalQuota(sum);
      setFlashRoute(true);
      setTimeout(() => setFlashRoute(false), 800);
      return;
    }
    setChooseTargetOpen(true);
  };

  const handleFillTarget = (target) => {
    const sum = computeSelectedQuota();
    if (sum === null) return;
    if (target === 'original') {
      setOriginalTotalQuota(sum);
      setFlashOriginal(true);
      setTimeout(() => setFlashOriginal(false), 800);
      setNextFillTarget('route');
    } else {
      setRouteTotalQuota(sum);
      setFlashRoute(true);
      setTimeout(() => setFlashRoute(false), 800);
      setNextFillTarget('original');
    }
    setChooseTargetOpen(false);
  };

  const handleCalcPercent = () => {
    if (!originalTotalQuota || originalTotalQuota <= 0) {
      showWarning(t('log.messages.original_missing'));
      return;
    }
    if (routeTotalQuota === null) {
      showWarning(t('log.messages.route_missing'));
      return;
    }
    // 路由总费用相对于原始总费用的变化百分比
    const change = ((routeTotalQuota - originalTotalQuota) / originalTotalQuota) * 100;
    const percentText =
      change === 0
        ? '0.00%'
        : `${change > 0 ? '+' : '-'}${Math.abs(change).toFixed(2)}%`;
    setDecreasePercent(percentText);
    setFlashPercent(true);
    setTimeout(() => setFlashPercent(false), 800);
  };

  const handleClearResult = () => {
    setOriginalTotalQuota(null);
    setRouteTotalQuota(null);
    setDecreasePercent('');
    setFlashOriginal(true);
    setFlashRoute(true);
    setFlashPercent(true);
    setTimeout(() => {
      setFlashOriginal(false);
      setFlashRoute(false);
      setFlashPercent(false);
    }, 800);
  };

  return (
    <>
      <Header as='h3'>
        {t('log.usage_details')}（{t('log.total_quota')}：
        {showStat && renderQuota(stat.quota, t)}
        {!showStat && (
          <span
            onClick={handleEyeClick}
            style={{ cursor: 'pointer', color: 'gray' }}
          >
            {t('log.click_to_view')}
          </span>
        )}
        ）
      </Header>
      <Form>
        <Form.Group>
          {/* <Form.Input
            fluid
            label={t('log.table.token_name')}
            size={'small'}
            width={3}
            value={token_name}
            placeholder={t('log.table.token_name_placeholder')}
            name='token_name'
            onChange={handleInputChange}
          />
          <Form.Input
            fluid
            label={t('log.table.model_name')}
            size={'small'}
            width={3}
            value={model_name}
            placeholder={t('log.table.model_name_placeholder')}
            name='model_name'
            onChange={handleInputChange}
          /> */}

          {isAdminUser && (
            <Form.Input
              fluid
              label={t('log.table.channel_id')}
              size={'small'}
              width={3}
              value={channel}
              placeholder={t('log.table.channel_id_placeholder')}
              name='channel'
              onChange={handleInputChange}
            />
          )}

          <Form.Input
            fluid
            label={t('log.table.start_time')}
            size={'small'}
            width={4}
            value={start_timestamp}
            type='datetime-local'
            name='start_timestamp'
            onChange={handleInputChange}
          />
          <Form.Input
            fluid
            label={t('log.table.end_time')}
            size={'small'}
            width={4}
            value={end_timestamp}
            type='datetime-local'
            name='end_timestamp'
            onChange={handleInputChange}
          />
          <Form.Button
            fluid
            label={t('log.buttons.query')}
            size={'small'}
            width={2}
            onClick={refresh}
          >
            {t('log.buttons.submit')}
          </Form.Button>
        </Form.Group>
        {/* {isAdminUser && (
          <>
            <Form.Group>
              <Form.Input
                fluid
                label={t('log.table.channel_id')}
                size={'small'}
                width={3}
                value={channel}
                placeholder={t('log.table.channel_id_placeholder')}
                name='channel'
                onChange={handleInputChange}
              />
              <Form.Input
                fluid
                label={t('log.table.username')}
                size={'small'}
                width={3}
                value={username}
                placeholder={t('log.table.username_placeholder')}
                name='username'
                onChange={handleInputChange}
              />
            </Form.Group>
          </>
        )} */}
        {/*<Form.Input
          icon='search'
          placeholder={t('log.search')}
          value={searchKeyword}
          onChange={(e, { value }) => setSearchKeyword(value)}
        />*/}

        <Form.Group>
          <Form.Input
            fluid
            label={t('log.calc.original_total')}
            readOnly
            width={3}
            value={originalTotalQuota !== null ? renderQuota(originalTotalQuota, t, 6) : ''}
            style={{ transition: 'background-color 0.8s', backgroundColor: flashOriginal ? '#fffbe6' : 'transparent' }}
          />
          <Form.Input
            fluid
            label={t('log.calc.route_total')}
            readOnly
            width={3}
            value={routeTotalQuota !== null ? renderQuota(routeTotalQuota, t, 6) : ''}
            style={{ transition: 'background-color 0.8s', backgroundColor: flashRoute ? '#fffbe6' : 'transparent' }}
          />
          <Form.Input
            fluid
            label={t('log.calc.decrease_percent')}
            readOnly
            width={3}
            value={decreasePercent}
            style={{ transition: 'background-color 0.8s', backgroundColor: flashPercent ? '#fffbe6' : 'transparent' }}
          />
          <Form.Field width={7}>
            <label>{t('log.buttons.query')}</label>
            <div>
              <Popup
                open={chooseTargetOpen}
                onClose={() => setChooseTargetOpen(false)}
                on='click'
                hoverable
                closeOnTriggerMouseLeave={false}
                content={
                  <div style={{ display: 'flex', gap: 8 }}>
                    <Button size='tiny' onClick={() => handleFillTarget('original')}>
                      {t('log.calc.original_total')}
                    </Button>
                    <Button size='tiny' onClick={() => handleFillTarget('route')}>
                      {t('log.calc.route_total')}
                    </Button>
                  </div>
                }
                position='top center'
                trigger={
                  <Button size='small' color='blue' onClick={() => {
                    if (originalTotalQuota === null || originalTotalQuota === 0 || routeTotalQuota === null) {
                      handleCalcCost();
                    } else {
                      setChooseTargetOpen(true);
                    }
                  }}>
                    {t('log.buttons.calc_cost')}
                  </Button>
                }
              />
              <Button size='small' color='teal' onClick={handleCalcPercent} style={{ marginLeft: 8 }}>
                {t('log.buttons.calc_percent')}
              </Button>
              <Button size='small' onClick={handleClearResult} style={{ marginLeft: 8 }}>
                {t('log.buttons.clear_result')}
              </Button>
            </div>
          </Form.Field>
        </Form.Group>
      </Form>
      <Table basic={'very'} compact size='small'>
        <Table.Header>
          <Table.Row>
            <Table.HeaderCell width={2}>
              <Checkbox
                checked={currentPageAllSelected}
                onChange={toggleSelectAllCurrentPage}
                label={t('log.table.select')}
              />
            </Table.HeaderCell>
            <Table.HeaderCell
              style={{ cursor: 'pointer' }}
              onClick={() => {
                sortLog('created_time');
              }}
              width={3}
            >
              {t('log.table.time')}
            </Table.HeaderCell>
            {isAdminUser && (
              <Table.HeaderCell
                style={{ cursor: 'pointer' }}
                onClick={() => {
                  sortLog('channel');
                }}
                width={1}
              >
                {t('log.table.channel')}
              </Table.HeaderCell>
            )}
            <Table.HeaderCell
              style={{ cursor: 'pointer' }}
              onClick={() => {
                sortLog('type');
              }}
              width={1}
            >
              {t('log.table.type')}
            </Table.HeaderCell>
            <Table.HeaderCell
              style={{ cursor: 'pointer' }}
              onClick={() => {
                sortLog('model_name');
              }}
              width={2}
            >
              {t('log.table.model')}
            </Table.HeaderCell>
            {showUserTokenQuota() && (
              <>
                {isAdminUser && (
                  <Table.HeaderCell
                    style={{ cursor: 'pointer' }}
                    onClick={() => {
                      sortLog('username');
                    }}
                    width={1}
                  >
                    {t('log.table.username')}
                  </Table.HeaderCell>
                )}
                <Table.HeaderCell
                  style={{ cursor: 'pointer' }}
                  onClick={() => {
                    sortLog('token_name');
                  }}
                  width={1}
                >
                  {t('log.table.token_name')}
                </Table.HeaderCell>
                <Table.HeaderCell
                  style={{ cursor: 'pointer' }}
                  onClick={() => {
                    sortLog('prompt_tokens');
                  }}
                  width={1}
                >
                  {t('log.table.prompt_tokens')}
                </Table.HeaderCell>
                <Table.HeaderCell
                  style={{ cursor: 'pointer' }}
                  onClick={() => {
                    sortLog('completion_tokens');
                  }}
                  width={1}
                >
                  {t('log.table.completion_tokens')}
                </Table.HeaderCell>
                <Table.HeaderCell
                  style={{ cursor: 'pointer' }}
                  onClick={() => {
                    sortLog('quota');
                  }}
                  width={1}
                >
                  {t('log.table.quota')}
                </Table.HeaderCell>
                <Table.HeaderCell
                  style={{ cursor: 'pointer' }}
                  onClick={() => {
                    sortLog('input_price');
                  }}
                  width={1}
                >
                  {t('log.table.input_price')}
                </Table.HeaderCell>
                <Table.HeaderCell
                  style={{ cursor: 'pointer' }}
                  onClick={() => {
                    sortLog('output_price');
                  }}
                  width={1}
                >
                  {t('log.table.output_price')}
                </Table.HeaderCell>
                <Table.HeaderCell
                  width={2}
                >
                  {t('log.table.request_body')}
                </Table.HeaderCell>
              </>
            )}
            <Table.HeaderCell>{t('log.table.detail')}</Table.HeaderCell>
          </Table.Row>
        </Table.Header>

        <Table.Body>
          {logs
            .slice(
              (activePage - 1) * itemsPerPage,
              activePage * itemsPerPage
            )
            .map((log, idx) => {
              if (log.deleted) return <></>;
              return (
                <Table.Row key={log.id}>
                  <Table.Cell>
                    <Checkbox
                      checked={selectedKeys.has(getLogKey(log))}
                      onChange={(e, { checked }) => toggleSelectOne(getLogKey(log), checked)}
                    />
                  </Table.Cell>
                  <Table.Cell>
                    {renderTimestamp(log.created_at, log.request_id)}
                  </Table.Cell>
                  {isAdminUser && (
                    <Table.Cell>
                      {log.channel ? (
                        <Label
                          basic
                          as={Link}
                          to={`/channel/edit/${log.channel}`}
                        >
                          {log.channel}
                        </Label>
                      ) : (
                        ''
                      )}
                    </Table.Cell>
                  )}
                  <Table.Cell>{renderType(log.type, t)}</Table.Cell>
                  <Table.Cell>
                    {log.model_name ? renderColorLabel(log.model_name) : ''}
                  </Table.Cell>
                  {showUserTokenQuota() && (
                    <>
                      {isAdminUser && (
                        <Table.Cell>
                          {log.username ? (
                            <Label
                              basic
                              as={Link}
                              to={`/user/edit/${log.user_id}`}
                            >
                              {log.username}
                            </Label>
                          ) : (
                            ''
                          )}
                        </Table.Cell>
                      )}
                      <Table.Cell>
                        {log.token_name ? renderColorLabel(log.token_name) : ''}
                      </Table.Cell>

                      <Table.Cell>
                        {log.prompt_tokens ? log.prompt_tokens : ''}
                      </Table.Cell>
                      <Table.Cell>
                        {log.completion_tokens ? log.completion_tokens : ''}
                      </Table.Cell>
                      <Table.Cell>
                        {log.quota ? renderQuota(log.quota, t, 6) : ''}
                      </Table.Cell>
                      <Table.Cell>
                        {log.input_price !== null && log.input_price !== undefined ? log.input_price : ''}
                      </Table.Cell>
                      <Table.Cell>
                        {log.output_price !== null && log.output_price !== undefined ? log.output_price : ''}
                      </Table.Cell>
                      <Table.Cell>
                        {log.messages ? (
                          <Popup
                            wide='very'
                            flowing
                            content={
                              <div
                                style={{
                                  width: '60vw',
                                  maxWidth: '800px',
                                  minWidth: '400px',
                                }}
                              >
                                <pre
                                  style={{
                                    maxHeight: '50vh',
                                    overflowY: 'auto',
                                    whiteSpace: 'pre-wrap',
                                    userSelect: 'text',
                                    margin: 0,
                                    padding: '10px',
                                    backgroundColor: '#f8f9fa',
                                    border: '1px solid #e9ecef',
                                    borderRadius: '4px',
                                  }}
                                >
                                  {log.messages}
                                </pre>
                              </div>
                            }
                            trigger={
                              <span style={{ cursor: 'pointer', color: '#007bff' }}>
                                {log.messages.slice(0, 60)}
                                {log.messages.length > 60 && '...'}
                              </span>
                            }
                            position={'top center'}
                            on='hover'
                            hoverable
                          />
                        ) : (
                          ''
                        )}
                      </Table.Cell>
                    </>
                  )}

                  <Table.Cell>{renderDetail(log)}</Table.Cell>
                </Table.Row>
              );
            })}
        </Table.Body>

        <Table.Footer>
          <Table.Row>
            <Table.HeaderCell colSpan={'10'}>
              <Select
                placeholder={t('log.table.page_size')}
                options={[
                  { key: 10, text: '10', value: 10 },
                  { key: 20, text: '20', value: 20 },
                  { key: 50, text: '50', value: 50 },
                  { key: 100, text: '100', value: 100 },
                ]}
                style={{ marginRight: '8px' }}
                value={itemsPerPage}
                onChange={(e, { value }) => {
                  setItemsPerPage(value);
                  setActivePage(1);
                }}
              />
              <Select
                placeholder={t('log.type.select')}
                options={LOG_OPTIONS}
                style={{ marginRight: '8px' }}
                name='logType'
                value={logType}
                onChange={(e, { name, value }) => {
                  setLogType(value);
                }}
              />
              <Button size='small' onClick={refresh} loading={loading}>
                {t('log.buttons.refresh')}
              </Button>
              <Pagination
                floated='right'
                activePage={activePage}
                onPageChange={onPaginationChange}
                size='small'
                siblingRange={1}
                totalPages={
                  Math.ceil(logs.length / itemsPerPage) +
                  (logs.length % itemsPerPage === 0 ? 1 : 0)
                }
              />
            </Table.HeaderCell>
          </Table.Row>
        </Table.Footer>
      </Table>
    </>
  );
};

export default LogsTable;
